import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedDate, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandS, LabelL, LabelS } from '@sorare/core/src/atoms/typography';
import { ResponsiveImg } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { Card } from '@sorare/core/src/components/card/Card';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';

import { Shape } from '#src/components/Wheel/Shape/index.tsx';
import { useWheelReward } from '#src/components/Wheel/utils.tsx';

import type {
  WheelHistoryQuery,
  WheelHistoryQueryVariables,
  WheelHistoryRow_probabilisticBundleSlotConfig,
  WheelHistoryRow_probabilisticBundleSlotItemInterface,
} from './__generated__/index.graphql.ts';

const CardWrapper = styled.div`
  width: 40px;
`;

const Row = styled.div`
  display: grid;
  grid-template-areas: 'illustration content';
  gap: var(--double-unit);
  height: 80px;
  grid-template-columns: 80px 1fr;
  align-items: center;
  justify-items: start;
  & > :first-child {
    align-self: stretch;
    justify-self: stretch;
    display: flex;
    & > :first-child {
      align-self: center;
      justify-self: center;
      margin: auto;
    }
  }
`;

const WheelHistoryRow = ({
  config,
  openedAt,
  rarity,
  items,
}: {
  config: WheelHistoryRow_probabilisticBundleSlotConfig;
  openedAt: Date;
  rarity: Rarity;
  items: WheelHistoryRow_probabilisticBundleSlotItemInterface[];
}) => {
  const {
    illustration,
    description: shortDescription,
    longDescription,
  } = useWheelReward(config);

  const description = longDescription || shortDescription;
  const card = isType(items[0], 'ProbabilisticBundleSlotCardItem')
    ? items[0].anyCard
    : null;

  return (
    <Row>
      <Shape config={config} rarity={rarity}>
        {card ? (
          <CardWrapper>
            <Card card={card} width={40} />
          </CardWrapper>
        ) : (
          <ResponsiveImg src={illustration} alt="" width={48} cropWidth={80} />
        )}
      </Shape>
      <Vertical style={{ alignItems: 'flex-start', width: '100%' }}>
        <BrandS>
          {description}
          {card ? <> · {card.anyPlayer.displayName}</> : null}
        </BrandS>
        <LabelS color="var(--c-nd-600)">
          <FormattedDate value={openedAt} dateStyle="short" timeStyle="short" />
        </LabelS>
      </Vertical>
    </Row>
  );
};

WheelHistoryRow.fragments = {
  probabilisticBundleSlotConfig: gql`
    fragment WheelHistoryRow_probabilisticBundleSlotConfig on ProbabilisticBundleSlotConfig {
      id
      ...useWheelReward_probabilisticBundleSlotConfig
      ...Shape_probabilisticBundleSlotConfig
    }
    ${Shape.fragments.ProbabilisticBundleSlotConfig}
    ${useWheelReward.fragments.probabilisticBundleSlotConfig}
  ` as TypedDocumentNode<WheelHistoryRow_probabilisticBundleSlotConfig>,
  probabilisticBundleSlotItemInterface: gql`
    fragment WheelHistoryRow_probabilisticBundleSlotItemInterface on ProbabilisticBundleSlotItemInterface {
      id
      ... on ProbabilisticBundleSlotCardItem {
        id
        anyCard {
          slug
          ...Card_anyCard
          anyPlayer {
            slug
            displayName
          }
        }
      }
    }
    ${Card.fragments.anyCard}
  ` as TypedDocumentNode<WheelHistoryRow_probabilisticBundleSlotItemInterface>,
};

const query = gql`
  query WheelHistoryQuery($sport: Sport!, $after: String, $rarity: Rarity) {
    currentUser {
      slug
      myWheelRewards(sport: $sport, after: $after, rarity: $rarity) {
        pageInfo {
          hasNextPage
          endCursor
        }
        nodes {
          id
          probabilisticBundle {
            id
            openedAt
            config {
              id
              rarity
              slots {
                probableConfigs {
                  id
                  ...WheelHistoryRow_probabilisticBundleSlotConfig
                }
              }
            }
            drawnSlotConfigIds
            items {
              id
              ...WheelHistoryRow_probabilisticBundleSlotItemInterface
            }
          }
        }
      }
    }
  }
  ${WheelHistoryRow.fragments.probabilisticBundleSlotConfig}
  ${WheelHistoryRow.fragments.probabilisticBundleSlotItemInterface}
` as TypedDocumentNode<WheelHistoryQuery, WheelHistoryQueryVariables>;

type Props = {
  rarity: Rarity;
};

export const WheelHistory = ({ rarity }: Props) => {
  const { sport } = useSportContext();
  const { data, loadMore } = usePaginatedQuery(query, {
    variables: { sport, rarity },
    connection: 'ProbabilisticBundleRewardConnection',
  });
  const currentUser = data?.currentUser;
  const pageInfo = currentUser?.myWheelRewards?.pageInfo;
  const hasNextPage = pageInfo?.hasNextPage;
  const endCursor = pageInfo?.endCursor ?? null;

  if (!currentUser?.myWheelRewards.nodes.length) return null;

  return (
    <div>
      <LabelL bold>
        <FormattedMessage
          defaultMessage="{nb, plural, 1 {Reward} other {Rewards}} obtained"
          values={{
            nb: currentUser.myWheelRewards.nodes.length,
          }}
        />
      </LabelL>
      <Vertical>
        {currentUser.myWheelRewards.nodes.map(node => {
          const rewards =
            node.probabilisticBundle.config.slots[0].probableConfigs.filter(
              slot =>
                node.probabilisticBundle.drawnSlotConfigIds.includes(slot.id)
            );
          return (
            <div key={node.id}>
              {rewards.map(reward => (
                <WheelHistoryRow
                  key={reward.id}
                  config={reward}
                  openedAt={node.probabilisticBundle.openedAt!}
                  rarity={node.probabilisticBundle.config.rarity}
                  items={node.probabilisticBundle.items}
                />
              ))}
            </div>
          );
        })}
        {!!hasNextPage && (
          <Button
            size="medium"
            onClick={async () => loadMore(false, { after: endCursor })}
            disabled={!hasNextPage}
          >
            <FormattedMessage {...glossary.loadMore} />
          </Button>
        )}
      </Vertical>
    </div>
  );
};
